import React from "react";
import AnalysisPanel from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import useClientPagination from "../../../hooks/useClientPagination";
import { getCiReportAbsorptionVelocity } from "../../../api/competitiveIntelligence";
import { categoryLabel } from "../../../utils/ciFormat";
import { PanelTakeaway, getDemandSignalTone, DEMAND_SIGNAL_TONE_CLASSES, fmtDate } from "../shared";
import ThinScroll from "../../shared/ThinScroll";

const PAGE_SIZE = 15;

// Map raw backend status codes to user-friendly labels. Anything not in the map
// is hidden rather than shown raw — we'd rather omit a line than leak an enum.
const STATUS_LABELS = {
  NEEDS_MULTIPLE_SYNCS: "Awaiting more sync data",
  NO_DATA: "No data yet",
  PENDING: "Pending",
};

// A1: Map raw backend date_window_type enum values to user-friendly labels.
// Anything not in the map falls through to the raw value rather than leaking
// an enum, but the three known values are the only ones expected.
const WINDOW_TYPE_LABELS = {
  weekday_sample: "Weekday",
  weekend: "Weekend",
  holiday: "Holiday",
};

// Shared "needs multiple syncs" placeholder — used by all five absorption
// panels (8T-X) since they all behave the same way before 2 sync cycles exist.
// Exported so other absorption panels can render it directly.
//
// B3: the RPC envelope carries `latest_sync_date` (the real scrape day — syncs
// land on irregular days, not the 1st of the month). Surface it as
// "Data as of …" so users know how stale the single sync is. Falls back to
// `current_sync` for back-compat with older payloads that only sent that field.
export const NeedsMultipleSyncs = ({ status, message, currentSync, latestSyncDate }) => {
  const friendlyStatus = status ? STATUS_LABELS[status] : null;
  const asOf = latestSyncDate || currentSync;
  return (
    <div className="p-8 flex flex-col items-center text-center">
      <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-3">
        <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">More data available after next sync cycle</h4>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-md">
        {message || "This report needs at least 2 sync cycles to compute."}
      </p>
      {asOf && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
          Data as of <span className="font-medium">{fmtDate(asOf)}</span>
          {friendlyStatus && (
            <>
              {" · "}
              <span className="font-medium">{friendlyStatus}</span>
            </>
          )}
        </p>
      )}
    </div>
  );
};

// Header overrides for fields whose underscore-to-space label would either
// violate copy policy or read poorly. Anything not listed gets Title Case from
// the auto transform below. The backend uses `projected_sellout_date`; we never
// surface "sellout" in user copy, so we re-label it.
const COLUMN_LABELS = {
  projected_sellout_date: "Projected Fill Date",
  category_code: "Category",
  velocity_signal: "Velocity Signal",
};

// Title-case an underscore_separated field name for use as a column header,
// preserving common acronyms ("ID", "URL").
const ACRONYMS = new Set(["id", "url", "rv", "api"]);
const headerLabel = (key) => {
  if (COLUMN_LABELS[key]) return COLUMN_LABELS[key];
  return key
    .split("_")
    .map((part) => (ACRONYMS.has(part) ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1)))
    .join(" ");
};

// Velocity signal coloring follows the demand-signal semantics: ACCELERATING
// = urgency (red), STEADY = healthy (emerald), SLOWING = caution (amber),
// STALLED = inert (gray). Keys are uppercased before lookup.
const VELOCITY_SIGNAL_CLASSES = {
  ACCELERATING: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  STEADY:       "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  SLOWING:      "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  STALLED:      "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
};

const VelocitySignalBadge = ({ value }) => {
  if (!value) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  const key = String(value).toUpperCase().trim();
  const cls = VELOCITY_SIGNAL_CLASSES[key] ?? "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
  const label = key.charAt(0) + key.slice(1).toLowerCase();
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold tracking-wide ${cls}`}>
      {label}
    </span>
  );
};

// Demand-signal badge for the dynamic column renderer. The absorption velocity
// RPC may surface a `demand_signal` column; route it through the shared keyword
// classifier so HIGH DEMAND renders red instead of plain text.
const DemandSignalBadge = ({ value }) => {
  if (!value) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  const cls = DEMAND_SIGNAL_TONE_CLASSES[getDemandSignalTone(value)].badge;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold ${cls}`}>
      {value}
    </span>
  );
};

// A1: Explicit column ordering. The velocity RPC returns up to 18 fields; we
// surface 10 by default and tuck the remaining 8 behind a per-row expand.
// Both lists are filtered against the actual payload keys at render time so a
// missing or renamed field degrades gracefully instead of breaking the table.
const VISIBLE_COLUMNS = [
  "date_window_name",
  "inventory_previous_sync",
  "inventory_current_sync",
  "units_absorbed",
  "absorption_rate_pct",
  "current_occupancy",
  "your_rate",
  "your_occupancy",
  "demand_signal",
  "velocity_signal",
];

const HIDDEN_COLUMNS = [
  "check_in",
  "date_window_type",
  "days_out",
  "category_code",
  "previous_occupancy",
  "competitors_sold_out",
  "competitors_min_stay",
  "total_competitors",
];

// Per-field cell formatters. Unlisted fields render via the generic fallback.
const CELL_FORMATTERS = {
  category_code: (value) => categoryLabel(value),
  velocity_signal: (value) => <VelocitySignalBadge value={value} />,
  demand_signal: (value) => <DemandSignalBadge value={value} />,
  date_window_type: (value) => (value ? (WINDOW_TYPE_LABELS[value] ?? value) : "—"),
};

// A1 (expand-tracker): Group the 8 hidden fields into labeled sections so the
// expanded area reads as a detail panel, not a dump of leftover columns.
const DETAIL_SECTIONS = [
  {
    title: "Timing",
    fields: ["check_in", "days_out", "date_window_type"],
  },
  {
    title: "Market Context",
    fields: ["category_code", "total_competitors", "competitors_sold_out", "competitors_min_stay"],
  },
  {
    title: "Previous State",
    fields: ["previous_occupancy"],
  },
];

const formatFieldValue = (row, column) => {
  const value = row[column];
  const isEmpty = value == null;
  const formatter = CELL_FORMATTERS[column];
  if (formatter) return formatter(value);
  if (isEmpty) return "—";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
};

const fieldIsEmpty = (row, column) => row[column] == null && !CELL_FORMATTERS[column];

const VelocityResultsTable = ({ results }) => {
  const [expandedRows, setExpandedRows] = React.useState(() => new Set());
  if (!Array.isArray(results) || results.length === 0) {
    return <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">No velocity rows returned.</div>;
  }
  const payloadKeys = new Set(Object.keys(results[0]));
  const visibleCols = VISIBLE_COLUMNS.filter((c) => payloadKeys.has(c));
  const hiddenCols = HIDDEN_COLUMNS.filter((c) => payloadKeys.has(c));
  const hasHidden = hiddenCols.length > 0;
  const toggleRow = (index) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };
  const handleRowKeyDown = (e, index) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleRow(index);
    }
  };
  const renderCell = (row, column) => {
    const value = row[column];
    const isEmpty = value == null;
    const formatter = CELL_FORMATTERS[column];
    let display;
    if (formatter) {
      display = formatter(value);
    } else if (isEmpty) {
      display = "—";
    } else {
      display = typeof value === "object" ? JSON.stringify(value) : String(value);
    }
    const title = isEmpty && !formatter ? "No data available" : undefined;
    return (
      <td
        key={column}
        data-label={headerLabel(column)}
        className={`px-3 py-2.5 text-gray-700 dark:text-gray-200 whitespace-nowrap${isEmpty && !formatter ? " cursor-help" : ""}`}
        title={title}
      >
        {display}
      </td>
    );
  };
  const colSpan = visibleCols.length + 1;
  return (
    <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
      <table className="w-full text-sm responsive-card-table">
        <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th className="px-2 py-2.5 text-left font-semibold w-8" aria-label="Expand row" />
            {visibleCols.map((column) => (
              <th key={column} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">{headerLabel(column)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {results.map((row, index) => {
            const isOpen = expandedRows.has(index);
            return (
              <React.Fragment key={index}>
                <tr
                  onClick={hasHidden ? () => toggleRow(index) : undefined}
                  onKeyDown={hasHidden ? (e) => handleRowKeyDown(e, index) : undefined}
                  role={hasHidden ? "button" : undefined}
                  tabIndex={hasHidden ? 0 : undefined}
                  aria-expanded={hasHidden ? isOpen : undefined}
                  className={`border-b dark:border-gray-800 transition-colors duration-150${
                    hasHidden ? " cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-800/50" : " hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  }${isOpen ? " bg-gray-50 dark:bg-gray-800/30" : ""}`}
                >
                  <td className="px-2 py-2.5 text-left">
                    {hasHidden && (
                      <svg
                        className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </td>
                  {visibleCols.map((column) => renderCell(row, column))}
                </tr>
                {hasHidden && (
                  <tr className="border-b dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30">
                    <td colSpan={colSpan} className="p-0">
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          isOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="mx-4 my-3 px-4 py-4 flex flex-wrap justify-center items-start gap-x-6 gap-y-2 text-xs border-t border-b border-gray-200 dark:border-gray-700">
                          {DETAIL_SECTIONS.map((section, sIdx) => {
                            const sectionFields = section.fields.filter((f) => payloadKeys.has(f));
                            if (sectionFields.length === 0) return null;
                            return (
                              <React.Fragment key={section.title}>
                                {sIdx > 0 && (
                                  <div className="self-stretch w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                                )}
                                <div className="flex flex-col gap-1">
                                  <h5 className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">
                                    {section.title}
                                  </h5>
                                  <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                                    {sectionFields.map((column) => {
                                      const isEmpty = fieldIsEmpty(row, column);
                                      const title = isEmpty ? "No data available" : undefined;
                                      return (
                                        <div key={column} className="flex items-baseline gap-1.5">
                                          <dt className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{headerLabel(column)}:</dt>
                                          <dd
                                            className={`text-gray-700 dark:text-gray-200 whitespace-nowrap font-medium${isEmpty ? " cursor-help" : ""}`}
                                            title={title}
                                          >
                                            {formatFieldValue(row, column)}
                                          </dd>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </ThinScroll>
  );
};

const VelocityResultsTablePaginated = ({ results }) => {
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(results, { pageSize: PAGE_SIZE });
  return (
    <>
      <VelocityResultsTable results={pageItems} />
      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="rows"
          />
        </div>
      )}
    </>
  );
};

const AbsorptionVelocityPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`absorption-velocity-${clientId}-${categoryCode || "all"}`}
    title="Absorption Velocity"
    tip="How quickly inventory is being booked up across the market between syncs. High velocity = the market is filling fast, usually a buy-signal to raise rates. Needs 2+ syncs to compute."
    description="How fast inventory is disappearing across the market — requires 2+ sync cycles."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportAbsorptionVelocity(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      if (!Array.isArray(data) && data?.status === "needs_multiple_syncs") {
        return <NeedsMultipleSyncs status={data.status} message={data.message} currentSync={data.current_sync} latestSyncDate={data.latest_sync_date} />;
      }
      const results = Array.isArray(data) ? data : (data?.items ?? []);
      if (!results.length) {
        return <NeedsMultipleSyncs status={data?.status} message={data?.message} currentSync={data?.current_sync} latestSyncDate={data?.latest_sync_date} />;
      }
      return (
        <>
          <PanelTakeaway panelKey="absorption_velocity" say={say} />
          <VelocityResultsTablePaginated results={results} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default AbsorptionVelocityPanel;
